import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n';

/**
 * Text-based brand logos per CRAFTFOOD brandbook.
 * Brand NAMES stay in latin (CRAFTFOOD / LAVASH / VELORA);
 * subtitles & taglines are LOCALIZED via i18n.
 */

export const CraftFoodLogo = ({ size = 'md', linkTo = '/', mono = false, dark = false, className = '' }) => {
  const { t } = useLanguage();
  const sizes = {
    sm: { brand: 'text-base', tag: 'text-[8px]' },
    md: { brand: 'text-2xl', tag: 'text-[10px]' },
    lg: { brand: 'text-4xl', tag: 'text-xs' },
    xl: { brand: 'text-6xl md:text-7xl', tag: 'text-sm' },
  };
  const s = sizes[size] || sizes.md;
  const baseColor = dark ? 'text-[#F6F4EE]' : 'text-[#1A1A1A]';
  const accentColor = mono ? baseColor : (dark ? 'text-[#C9A352]' : 'text-[#C9A352]');
  const tagColor = dark ? 'text-[#F6F4EE]/70' : 'text-[#1A1A1A]/60';
  const borderColor = dark ? 'border-[#F6F4EE]/30' : 'border-[#1A1A1A]/20';

  const content = (
    <div className={`inline-block ${className}`} data-testid="craftfood-logo">
      <div className="flex items-baseline gap-1.5 leading-none">
        <span className={`${s.tag} ${tagColor} font-light tracking-widest uppercase`}>by</span>
        <span className={`${s.brand} ${baseColor} font-extrabold tracking-tight font-montserrat`}>
          CRAFT<span className={accentColor}>FOOD</span>
        </span>
      </div>
      <div className={`border-t ${borderColor} mt-1.5 pt-1.5 ${s.tag} ${tagColor} tracking-[0.25em] uppercase font-medium`}>
        {t('tagCraftfood')}
      </div>
    </div>
  );

  if (linkTo) return <Link to={linkTo} className="inline-block">{content}</Link>;
  return content;
};

export const LavashLogo = ({ size = 'md', dark = false, className = '' }) => {
  const { t } = useLanguage();
  const sizes = {
    sm: { brand: 'text-lg', sub: 'text-[8px]', tag: 'text-[7px]' },
    md: { brand: 'text-3xl', sub: 'text-[10px]', tag: 'text-[9px]' },
    lg: { brand: 'text-6xl md:text-7xl', sub: 'text-sm', tag: 'text-xs' },
  };
  const s = sizes[size] || sizes.md;
  const main = dark ? 'text-[#A4B18E]' : 'text-[#556B2F]';
  const sub = dark ? 'text-[#A4B18E]' : 'text-[#556B2F]';
  const border = dark ? 'border-[#A4B18E]/60' : 'border-[#A4B18E]';
  return (
    <div className={`inline-block ${className}`} data-testid="lavash-logo">
      <div className={`${s.brand} ${main} font-extrabold tracking-tight leading-none font-montserrat`}>LAVASH</div>
      <div className={`border-t ${border} mt-1.5 pt-1 ${s.sub} ${sub} tracking-[0.25em] uppercase font-semibold`}>
        {t('tagLavashSub')}
      </div>
      <div className={`mt-1.5 ${s.tag} ${sub}/80 tracking-[0.2em] uppercase font-medium`}>
        {t('tagLavashSlogan')}
      </div>
    </div>
  );
};

export const VeloraLogo = ({ size = 'md', dark = false, className = '' }) => {
  const { t } = useLanguage();
  const sizes = {
    sm: { brand: 'text-lg', sub: 'text-[8px]', tag: 'text-[7px]' },
    md: { brand: 'text-3xl', sub: 'text-[10px]', tag: 'text-[9px]' },
    lg: { brand: 'text-6xl md:text-7xl', sub: 'text-sm', tag: 'text-xs' },
  };
  const s = sizes[size] || sizes.md;
  const main = dark ? 'text-[#E8C5C8]' : 'text-[#8E2A4E]';
  const sub = dark ? 'text-[#E8C5C8]' : 'text-[#6E4D3A]';
  return (
    <div className={`inline-block ${className}`} data-testid="velora-logo">
      <div className={`${s.brand} ${main} font-bold italic tracking-tight leading-none font-playfair`}>VELORA</div>
      <div className={`mt-2 ${s.sub} ${sub} tracking-[0.3em] uppercase font-medium`}>
        {t('tagVeloraSub')}
      </div>
      <div className={`mt-1 ${s.tag} ${main}/80 tracking-[0.2em] uppercase font-medium`}>
        {t('tagVeloraSlogan')}
      </div>
    </div>
  );
};
